import moment from 'moment'
import { DATE_FORMAT } from '@/app/constants/date'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'

interface UseExchangeRateHandlerProps {
  companyId: string
  onUpdateArrayItem: (arrayName: string, index: number, updates: any) => void
  arrayName: string
}

export const useExchangeRateHandler = ({
  companyId,
  onUpdateArrayItem,
  arrayName,
}: UseExchangeRateHandlerProps) => {
  const { t } = useLocalize()
  const { getExchangeRate, changeExchangeRates } = useBusinessTripService()

  const handleCurrencyChange = async (
    item: any,
    originalIndex: number,
    newCurrencyCode: string,
    newCurrencyId: string | number,
  ) => {
    const currency = { code: newCurrencyCode, id: newCurrencyId }
    if (newCurrencyCode.toUpperCase() !== 'PLN') {
      try {
        const exchangeRate = await getExchangeRate({
          companyId,
          exchangeRateDate: moment(item.exchangeRateDate),
          currencyCode: newCurrencyCode,
        })
        if (exchangeRate != null) {
          onUpdateArrayItem(arrayName, originalIndex, {
            exchangeRate: exchangeRate.rate,
            enteredManually: exchangeRate.enteredManually,
            exchangeRateDate: exchangeRate.exchangeRateDate,
            currency,
            table: exchangeRate.table,
            plnAmount: item.amount * exchangeRate.rate,
          })
        }
      } catch {
        // silently fail - rate fetch error
      }
    } else {
      onUpdateArrayItem(arrayName, originalIndex, {
        currency,
        table: '',
        exchangeRate: 1,
        plnAmount: item.amount,
      })
    }
  }

  const handleExchangeRateDateChange = async (
    item: any,
    originalIndex: number,
    newDate: string,
  ) => {
    const exchangeRateDate = moment(newDate)
    if (
      item.currency != null &&
      item.currency.code?.toUpperCase() !== 'PLN'
    ) {
      try {
        const exchangeRate = await getExchangeRate({
          companyId,
          exchangeRateDate,
          currencyCode: item.currency.code,
        })
        if (exchangeRate != null) {
          onUpdateArrayItem(arrayName, originalIndex, {
            exchangeRate: exchangeRate.rate,
            exchangeRateDate: exchangeRate.exchangeRateDate,
            enteredManually: exchangeRate.enteredManually,
            table: exchangeRate.table,
            plnAmount: item.amount * exchangeRate.rate,
          })
        }
      } catch {
        // silently fail - rate fetch error
      }
    } else {
      onUpdateArrayItem(arrayName, originalIndex, {
        exchangeRateDate,
        table: '',
        plnAmount: item.amount,
      })
    }
  }

  const handleExchangeRateManualChange = async (
    item: any,
    originalIndex: number,
    newRate: number,
  ) => {
    if (newRate !== item.exchangeRate) {
      onUpdateArrayItem(arrayName, originalIndex, {
        exchangeRate: newRate,
        plnAmount: item.amount * newRate,
        enteredManually: true,
      })
      try {
        await changeExchangeRates({
          companyId,
          rates: [
            {
              exchangeRateDate: moment(item.exchangeRateDate),
              currencyCode: item.currency.code,
              rate: newRate,
            },
          ],
        })
        toast.info({
          title:
            t('newDelegation.change-exchange-rate-title'),
          text: (
            t('newDelegation.change-exchange-rate-details')
          )
            .replace('{0}', item.currency.code)
            .replace('{1}', moment(item.exchangeRateDate).format(DATE_FORMAT)),
        })
      } catch {
        // silently fail
      }
    }
  }

  const handleAmountChange = (
    item: any,
    originalIndex: number,
    newAmount: number,
  ) => {
    onUpdateArrayItem(arrayName, originalIndex, {
      amount: newAmount,
      plnAmount: newAmount * (item.exchangeRate || 1),
    })
  }

  return {
    handleCurrencyChange,
    handleExchangeRateDateChange,
    handleExchangeRateManualChange,
    handleAmountChange,
  }
}
